import { baseApi } from './baseApi';

export interface UIBlockSlot {
  id: string;
  uiBlockId: string;
  name: string;
  displayName: string;
  isRepeatable: boolean;
  minItems: number;
  maxItems: number | null;
  sortOrder: number;
  description: string;
  createdAt: string;
  allowedChildren: string[];
}

export interface UIBlock {
  id: string;
  name: string;
  html: string;
  css: string;
  js?: string | null;
  isPremium: boolean;
  schema: {
    editableFields: Record<string, string>;
  };
  version: string;
  isContainer: boolean;
  slots?: UIBlockSlot[];
  
  // Legacy fields for compatibility
  description?: string;
  category?: 'hero' | 'features' | 'pricing' | 'testimonials' | 'contact' | 'footer' | 'navigation';
  htmlContent?: string;
  cssContent?: string;
  jsContent?: string | null;
  thumbnail?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface UIBlocksListParams {
  category?: string;
  premium?: boolean;
}

export interface PageBlock {
  id: string;
  blockId: string;
  position: number;
  overrides: Record<string, any>;
  customHtml?: string;
}

export interface UpdatePageBlocksRequest {
  blocks: Array<{
    blockId: string;
    overrides: Record<string, any>;
    customHtml?: string;
  }>;
}

export interface PageBlocksResponse {
  id: string;
  slug: string;
  title: string;
  blocks: Array<{
    blockId: string;
    overrides: Record<string, any>;
    customHtml?: string;
  }>;
  htmlContent: string;
  customerId: string;
}

export const uiBlocksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUIBlocks: builder.query<UIBlock[], UIBlocksListParams>({
      query: (params) => ({
        url: '/ui-blocks',
        params,
      }),
      transformResponse: (response: { success: boolean; data: UIBlock[] }) => response.data,
      providesTags: ['UIBlock'],
    }),

    getUIBlockById: builder.query<UIBlock, string>({
      query: (id) => `/ui-blocks/${id}`,
      transformResponse: (response: { success: boolean; data: UIBlock }) => response.data,
      providesTags: (result, error, id) => [{ type: 'UIBlock', id }],
    }),

    updatePageBlocks: builder.mutation<PageBlocksResponse, { pageId: string; data: UpdatePageBlocksRequest }>({
      query: ({ pageId, data }) => ({
        url: `/pages/${pageId}/blocks`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: PageBlocksResponse }) => response.data,
      invalidatesTags: (result, error, { pageId }) => [
        { type: 'LandingPage', id: pageId },
        'LandingPage',
      ],
    }),

    generatePagePreview: builder.mutation<{ htmlContent: string }, { title: string; blocks: Array<{ blockId: string; overrides: Record<string, any>; customHtml?: string }> }>({
      query: ({ title, blocks }) => ({
        url: '/pages/preview',
        method: 'POST',
        body: { title, blocks },
        responseHandler: 'text',
      }),
      transformResponse: (response: string) => ({ htmlContent: response }),
    }),
  }),
});

export const {
  useGetUIBlocksQuery,
  useGetUIBlockByIdQuery,
  useUpdatePageBlocksMutation,
  useGeneratePagePreviewMutation,
} = uiBlocksApi;